import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { sendEmailVerification, onAuthStateChanged, User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useHeader } from '../../contexts/HeaderContext';
import ConfirmDialog from '../common/ConfirmDialog';

const COOLDOWN_TIME = 60; // 冷却时间（秒）

const EmailVerificationPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [cooldown, setCooldown] = useState(0);
    const [user, setUser] = useState<User | null>(null);
    const { setHeaderTheme } = useHeader();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [checking, setChecking] = useState(false);
    const [showNotVerifiedDialog, setShowNotVerifiedDialog] = useState(false);

    useEffect(() => {
        setHeaderTheme({
            bgColor: 'bg-[#4f46e5]',
            textColor: 'text-white'
        });
        
        return () => {
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (!currentUser) {
                navigate('/login');
                return;
            }
            
            if (currentUser.emailVerified) {
                navigate('/profile');
                return;
            }

            setUser(currentUser);
        });

        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (cooldown > 0) {
            timer = setInterval(() => {
                setCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [cooldown]);

    const handleResendEmail = async () => {
        if (!user || cooldown > 0) return;

        try {
            await sendEmailVerification(user);
            setCooldown(COOLDOWN_TIME);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('发送验证邮件失败:', error);
            setShowErrorDialog(true);
        }
    };

    const handleCheckEmail = () => {
        const email = user?.email;
        if (!email) return;

        const emailDomain = email.split('@')[1];
        const emailProviders: { [key: string]: string } = {
            'gmail.com': 'https://mail.google.com',
            'outlook.com': 'https://outlook.live.com',
            'hotmail.com': 'https://outlook.live.com',
            'yahoo.com': 'https://mail.yahoo.com',
        };

        const mailUrl = emailProviders[emailDomain] || `https://${emailDomain}`;
        window.open(mailUrl, '_blank');
    };

    const handleVerifyComplete = async () => {
        if (!user || checking) return;
        
        setChecking(true);
        try {
            // 刷新用户状态以获取最新的验证状态
            await user.reload();
            const currentUser = auth.currentUser;
            
            if (currentUser?.emailVerified) {
                navigate('/profile');
            } else {
                setShowNotVerifiedDialog(true);
            }
        } catch (error) {
            console.error('检查验证状态失败:', error);
            setShowNotVerifiedDialog(true);
        } finally {
            setChecking(false);
        }
    };

    if (!user) {
        return null;
    }

    return (
        <>
            <div className="min-h-screen bg-white">
                {/* 顶部渐变背景 */}
                <div className="relative bg-[linear-gradient(to_bottom,#4f46e5_0%,#3b82f6_100%)] h-[400px]">
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-48 pb-32">
                        <div className="text-center">
                            <h1 className="text-3xl font-bold text-white sm:text-4xl">
                                {t('emailVerification.title')}
                                <span className="block text-xl font-normal text-blue-100 mt-2">
                                    {t('emailVerification.subtitle')}
                                </span>
                            </h1>
                        </div>
                    </div>

                    {/* 曲线过渡 */}
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg 
                            className="w-full h-16 fill-white" 
                            viewBox="0 0 1440 54" 
                            preserveAspectRatio="none"
                        >
                            <path d="M0,0 C480,54 960,54 1440,0 L1440,54 L0,54 Z" />
                        </svg>
                    </div>
                </div>

                {/* 主要内容区域 */}
                <div className="relative -mt-20 z-10 pb-12">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                            <div className="p-8">
                                <div className="text-center">
                                    <p className="text-lg text-gray-600 mb-8">
                                        {t('emailVerification.description')}
                                    </p>
                                    <div className="space-y-4">
                                        <button
                                            onClick={handleCheckEmail}
                                            className="w-full sm:w-auto px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            {t('emailVerification.checkInbox')}
                                        </button>
                                        <div>
                                            <button
                                                onClick={handleResendEmail}
                                                disabled={cooldown > 0}
                                                className={`w-full sm:w-auto px-6 py-3 border border-gray-300 text-base font-medium rounded-md 
                                                    ${cooldown > 0 
                                                        ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                                                        : 'text-gray-700 bg-white hover:bg-gray-50'
                                                    } 
                                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                                            >
                                                {cooldown > 0 
                                                    ? `${cooldown} ${t('emailVerification.resendWait')}`
                                                    : t('emailVerification.resendEmail')
                                                }
                                            </button>
                                        </div>
                                        <div className="pt-4 border-t border-gray-200">
                                            <button
                                                onClick={handleVerifyComplete}
                                                disabled={checking}
                                                className="w-full sm:w-auto px-6 py-3 text-base font-medium text-blue-600 hover:text-blue-500"
                                            >
                                                {checking 
                                                    ? t('emailVerification.verifyChecking')
                                                    : t('emailVerification.verifyComplete')
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 成功对话框 */}
            <ConfirmDialog
                open={showSuccessDialog}
                title={t('emailVerification.resendSuccess')}
                message={t('emailVerification.successMessage')}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                onConfirm={() => setShowSuccessDialog(false)}
                onCancel={() => setShowSuccessDialog(false)}
                type="info"
            />

            {/* 错误对话框 */}
            <ConfirmDialog
                open={showErrorDialog}
                title={t('emailVerification.resendError')}
                message={t('emailVerification.errorMessage')}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                onConfirm={() => setShowErrorDialog(false)}
                onCancel={() => setShowErrorDialog(false)}
                type="danger"
            />

            {/* 未验证提示对话框 */}
            <ConfirmDialog
                open={showNotVerifiedDialog}
                title={t('emailVerification.verifyError')}
                message={t('emailVerification.verifyNotComplete')}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                onConfirm={() => setShowNotVerifiedDialog(false)}
                onCancel={() => setShowNotVerifiedDialog(false)}
                type="warning"
            />
        </>
    );
};

export default EmailVerificationPage; 